// src/components/instructor/ops/SyncNowPanel.js
//
// Pass 57C — ops Sync Now + sync-health panel, wired to the Pass-57B server callable (functions/
// syncNow.js). NEVER fakes a sync: when the callable is not deployed it says so. Only owner/admin/ops
// see the button. No PII.

import React, { useEffect, useState } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../firebase';
import { useAuth } from '../../../AuthContext';
import { canOperateOrganization } from '../../../utils/platformAccess';

// Item 5: "last success" must be authoritative — a dedicated query, not "the newest success within the
// last N runs". Requires a composite index (organizationId ASC, status ASC, finishedAt DESC) — see
// firestore.indexes plan for 57D. Until deployed the query still works in most cases; on an index error
// we fail safe to WARNING (never invent a success).
// Item 10: cadence-aware staleness. Automated cadence is 07:00/12:00/19:00 JST → max healthy gap 12h
// (19:00→07:00) + 1h grace = 13h. (24h would let a whole missed day read HEALTHY.)
export const CADENCE_STALE_MS = 13 * 60 * 60 * 1000;

// Item 4: classify a Functions error to a safe, correct message. Specific codes BEFORE any catch-all.
export function syncErrorMessage(err) {
  const code = String(err?.code || '');
  const msg = String(err?.message || '');
  if (/permission-denied/.test(code)) return 'You do not have permission to run sync.';
  if (/unauthenticated/.test(code)) return 'Please sign in again.';
  if (/failed-precondition/.test(code) || /SYNC_NOT_DEPLOYED/.test(msg)) return 'Sync service is not deployed in this environment yet (Pass 57D).';
  if (/not-found/.test(code)) return 'Sync service not found — deployment error.';
  if (/unavailable/.test(code)) return 'Sync service temporarily unavailable — try again shortly.';
  return 'Sync error — please try again.';
}

const tsMs = (ts) => { try { const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : (ts?._seconds ? new Date(ts._seconds * 1000) : null)); return d ? d.getTime() : null; } catch { return null; } };
// Pass 58F2 — SAS operates in Japan; show the canonical Asia/Tokyo (JST) time, not the viewer's browser TZ.
const fmt = (ts) => { const m = tsMs(ts); if (!m) return '—'; try { return `${new Date(m).toLocaleString('en-GB', { timeZone: 'Asia/Tokyo', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} JST`; } catch { return new Date(m).toLocaleString(); } };

export default function SyncNowPanel({ organizationId }) {
  const { userData } = useAuth() || {};
  const canOps = canOperateOrganization(userData, organizationId); // owner/admin/ops
  const [latestAttempt, setLatestAttempt] = useState(null);
  const [lastSuccess, setLastSuccess] = useState(null);
  const [inFlight, setInFlight] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!organizationId) return undefined;
    const base = [collection(db, 'syncRuns'), where('organizationId', '==', organizationId)];
    // Item 5: two INDEPENDENT authoritative queries.
    const qAttempt = query(...base, orderBy('finishedAt', 'desc'), limit(1));
    const qSuccess = query(...base, where('status', '==', 'success'), orderBy('finishedAt', 'desc'), limit(1));
    const u1 = onSnapshot(qAttempt, (s) => setLatestAttempt(s.docs[0]?.data() || null), () => {});
    const u2 = onSnapshot(qSuccess, (s) => setLastSuccess(s.docs[0]?.data() || null), () => setLastSuccess(null));
    return () => { u1(); u2(); };
  }, [organizationId]);

  const successMs = tsMs(lastSuccess?.finishedAt);
  const staleMs = successMs ? (Date.now() - successMs) : Infinity;
  const status = inFlight ? 'SYNCING'
    : latestAttempt?.status === 'error' ? 'ERROR'
    : !lastSuccess ? 'WARNING'
    : staleMs > CADENCE_STALE_MS ? 'WARNING'
    : 'HEALTHY';
  const statusColor = { HEALTHY: '#16A34A', SYNCING: '#2563EB', WARNING: '#B45309', ERROR: '#DC2626' }[status];

  const onSyncNow = async () => {
    if (inFlight) return; // prevent hammering while a request is in flight
    setInFlight(true); setMsg('');
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'syncNow');
      const res = await fn({ organizationId });
      const d = res.data || {};
      if (d.status === 'already_syncing') setMsg('Already syncing — a run is in progress.');
      else if (d.status === 'success') setMsg(`Synced: ${d.created || 0} new · ${d.updated || 0} updated · ${d.unchanged || 0} unchanged · ${d.needsReview || 0} review`);
      else setMsg(`Sync returned: ${d.status || 'unknown'}`);
    } catch (e) {
      setMsg(syncErrorMessage(e)); // fail LOUDLY — never fake success
    } finally { setInFlight(false); }
  };

  return (
    <div data-testid="sync-now-panel" style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 14, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13 }}>Schedule sync</div>
          <div style={{ fontSize: 11, color: '#6B7280' }}>Last synced: {fmt(lastSuccess?.finishedAt)}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF' }}>Runs automatically at 07:00 · 12:00 · 19:00 JST</div>
        </div>
        <span data-testid="sync-status" style={{ fontWeight: 700, color: statusColor }}>{status === 'HEALTHY' ? 'Healthy' : status === 'SYNCING' ? 'Syncing…' : status === 'ERROR' ? 'Error' : 'Needs attention'}</span>
      </div>
      {lastSuccess && (
        <div style={{ fontSize: 11, color: '#6B7280', marginTop: 8 }}>
          {((lastSuccess.created || 0) + (lastSuccess.updated || 0)) === 0
            ? `${lastSuccess.unchanged || 0} client bookings checked · no changes`
            : `${lastSuccess.created || 0} new · ${lastSuccess.updated || 0} updated · ${lastSuccess.unchanged || 0} unchanged`}
          {(lastSuccess.needsReview || 0) > 0 ? ` · ${lastSuccess.needsReview} to review` : ''}
        </div>
      )}
      {/* Pass 59B — the operational schedule-grid lane is distinct from client bookings (holds are provisional
          instructor commitments, never counted as client lessons). Shown only once a sync has recorded it. */}
      {lastSuccess && lastSuccess.grid && (lastSuccess.grid.standalone != null) && (
        <div style={{ fontSize: 11, color: '#7C3AED', marginTop: 4 }} data-testid="sync-grid-status">
          {(lastSuccess.grid.standalone || 0)} operational holds{(lastSuccess.grid.covered || 0) > 0 ? ` · ${lastSuccess.grid.covered} covered by bookings` : ''}
        </div>
      )}
      {/* Pass 59C — safe assignment reconciliation from schedule evidence (shown only when it did something). */}
      {lastSuccess && lastSuccess.grid && ((lastSuccess.grid.assignmentsApplied || 0) > 0 || (lastSuccess.grid.assignmentConflicts || 0) > 0) && (
        <div style={{ fontSize: 11, color: (lastSuccess.grid.assignmentConflicts || 0) > 0 ? '#C97D10' : '#1D9E75', marginTop: 4 }} data-testid="sync-assignment-status">
          {(lastSuccess.grid.assignmentsApplied || 0) > 0 ? `${lastSuccess.grid.assignmentsApplied} assignment${lastSuccess.grid.assignmentsApplied === 1 ? '' : 's'} reconciled from schedule` : ''}
          {(lastSuccess.grid.assignmentConflicts || 0) > 0 ? `${(lastSuccess.grid.assignmentsApplied || 0) > 0 ? ' · ' : ''}${lastSuccess.grid.assignmentConflicts} assignment conflict${lastSuccess.grid.assignmentConflicts === 1 ? '' : 's'} need review` : ''}
        </div>
      )}
      {latestAttempt?.status === 'error' && (
        <div style={{ fontSize: 11, color: '#B45309', marginTop: 6 }}>Last attempt failed: {fmt(latestAttempt?.finishedAt)}</div>
      )}
      {canOps && (
        <button data-testid="sync-now-btn" type="button" onClick={onSyncNow} disabled={inFlight}
          style={{ marginTop: 10, padding: '6px 14px', borderRadius: 6, border: 'none', cursor: inFlight ? 'default' : 'pointer', background: inFlight ? '#9CA3AF' : '#111827', color: '#fff', fontWeight: 600 }}>
          {inFlight ? 'Syncing…' : 'Sync Now'}
        </button>
      )}
      {msg && <div data-testid="sync-msg" style={{ fontSize: 11, color: '#374151', marginTop: 8 }}>{msg}</div>}
    </div>
  );
}
